<template>
  <v-container class="px-10 news-page" fluid>
    <v-row>
      <v-col class="py-0">
        <v-breadcrumbs
          :items="breadcrumbs"
          divider=">"
          class="px-0 py-2"
        ></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div class="head-left">
        <h3 class="pageHeadline mb-1" t-data="page-headline">
          News & Articles <span class="thai-subtitle">ข่าวสารและบทความ</span>
        </h3>
        <p class="page-description mb-0">
          Publish health articles, hospital news, promotions and press releases to branch sites and the central portal.
        </p>
      </div>

      <v-btn
        color="primary"
        class="add-btn"
        depressed
        @click="goToCreateArticle"
        t-data="create-btn"
      >
        <v-icon left small>mdi-plus</v-icon>
        Create New Article
      </v-btn>
    </div>

    <div class="pill-tabs-bar mb-4">
      <button
        type="button"
        :class="['tab-pill', { active: isArticleListTab }]"
        @click="goToArticlesTab"
      >
        Articles List
      </button>
      <button
        type="button"
        :class="['tab-pill', { active: isCategoriesTab }]"
        @click="goToCategoriesTab"
      >
        Categories & Tags Setup
      </button>
    </div>

    <v-card class="table-card" elevation="0">
      
      <div class="filter-bar pa-4">
        <div class="filter-row d-flex align-center gap-2">
          
          <!-- Search Field  -->
          <v-text-field
            v-model="searchQuery"
            placeholder="Search by Title, Keywords, or Author..."
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            color="primary"
            class="filter-search-field flex-grow-1"
            @input="onSearchInput"
            @keyup.enter="handleSearch"
            @click:clear="clearSearch"
          ></v-text-field>

          <!-- Branch Dropdown -->
          <v-select
            v-model="selectedBranch"
            :items="branchSelectItems"
            item-text="name"
            item-value="codename"
            outlined
            dense
            hide-details
            color="primary"
            item-color="primary"
            :disabled="isBranchDisabled"
            class="filter-select-field branch-select"
            @change="handleBranchChange"
          ></v-select>

          <!-- Category Dropdown -->
          <v-select
            v-model="selectedCategory"
            :items="categoryOptions"
            outlined
            dense
            hide-details
            color="primary"
            class="filter-select-field category-select"
            @change="handleFilterChange"
          ></v-select>

          <!-- Status Dropdown -->
          <v-select
            v-model="selectedStatus"
            :items="statusOptions"
            outlined
            dense
            hide-details
            color="primary"
            class="filter-select-field status-select"
            @change="handleFilterChange"
          ></v-select>

          <!-- Date Range Picker -->
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <div class="date-range-container d-flex align-center" v-bind="attrs" v-on="on">
                <v-icon small class="date-icon mr-2">mdi-calendar-range-outline</v-icon>
                <input
                  v-model="dateFrom"
                  type="text"
                  placeholder="dd/mm/yyyy"
                  class="date-input"
                  readonly
                />
                <span class="date-separator mx-2">—</span>
                <input
                  v-model="dateTo"
                  type="text"
                  placeholder="dd/mm/yyyy"
                  class="date-input"
                  readonly
                />
              </div>
            </template>
            <v-date-picker
              v-model="pickerDates"
              range
              no-title
              scrollable
              color="primary"
              @input="onDateRangeChange"
            ></v-date-picker>
          </v-menu>
          <v-btn
            v-if="hasAppliedFilters"
            outlined
            class="filter-btn has-filtered text-none"
            height="40"
            @click="clearAllFilters"
          >
            <v-icon small left>mdi-close</v-icon>
            <span>Clear</span>
          </v-btn>

        </div>

      </div>

      <!-- Articles Data Table -->
      <v-data-table
        :headers="table.headers"
        :items="formattedNewsList"
        :loading="isLoading"
        :items-per-page="table.pageSize"
        :server-items-length="totalDisplayCount"
        hide-default-footer
        fixed-header
        class="news-table"
      >
        <template v-slot:item.no="{ item }">
          <span class="no-text">{{ getRowIndex(item) }}</span>        
        </template>

        <template v-slot:item.article="{ item }">
          <div class="article-cell d-flex align-center py-2">
            <div class="article-icon-box" :style="{ backgroundColor: getArticleColor(item) }">
              <v-icon color="#fff" size="18">mdi-newspaper-variant-outline</v-icon>
            </div>
            <div class="article-info ml-3">
              <div class="article-title-th">{{ item.title_th }}</div>
              <div class="article-title-en">{{ item.title_en }}</div>
            </div>
          </div>
        </template>

        <template v-slot:item.org_codename="{ item }">
          <span :class="['branch-tag', isCentralOrg(item.org_codename) ? 'central' : 'branch']">
            <template v-if="isCentralOrg(item.org_codename)">
              PRINCGroup<br>(Central)
            </template>
            <template v-else>
              {{ (item.org_codename || '').toUpperCase() }}
            </template>
          </span>
        </template>

        <template v-slot:item.category="{ item }">
          <span class="category-pill">
            {{ item.category_name || item.category }}
          </span>
        </template>

        <template v-slot:item.author="{ item }">
          <span class="author-text">{{ item.author || '-' }}</span>
        </template>

        <template v-slot:item.is_featured="{ item }">
          <span v-if="item.is_featured" class="featured-pill pinned">
            ★ Pinned
          </span>
          <span v-else class="featured-pill standard">
            Standard
          </span>
        </template>

        <template v-slot:item.publish_date="{ item }">
          <div v-if="item.publish_date" class="date-cell">
            <div class="date-text">{{ formatDate(item.publish_date) }}</div>
            <div class="time-text">{{ formatTime(item.publish_date) }}</div>
          </div>
          <span v-else class="text-grey">—</span>
        </template>

        <template v-slot:item.status="{ item }">
          <span :class="['status-pill', `status-${getStatusColor(item.status)}`]">
            <span class="dot"></span>
            {{ formatStatusLabel(item.status) }}
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="action-buttons d-flex align-center gap-1">
            <button
              class="action-icon-btn edit-btn"
              title="Edit"
              :disabled="loadingEditId === item.id"
              @click="handleEditNewsArticle(item)"
            >
              <v-progress-circular
                v-if="loadingEditId === item.id"
                indeterminate
                size="13"
                width="2"
                color="#2563eb"
              ></v-progress-circular>
              <v-icon v-else size="15">mdi-pencil-outline</v-icon>
            </button>
            <button class="action-icon-btn preview-btn" title="Preview" @click="handlePreview(item)">
              <v-icon size="15">mdi-eye-outline</v-icon>
            </button>
            <button class="action-icon-btn delete-btn" title="Delete" @click="handleDelete(item)">
              <v-icon size="15">mdi-delete-outline</v-icon>
            </button>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="py-10 text-center grey--text">
            <v-icon size="44" color="grey lighten-1">mdi-newspaper-variant-outline</v-icon>
            <div class="mt-2 text-caption font-weight-medium">No articles found</div>
          </div>
        </template>
      </v-data-table>

      <div class="table-foot">
        <span class="footer-entries">
          Showing {{ pageStart }} to {{ pageEnd }} of {{ totalDisplayCount  }} entries
        </span>
        <v-pagination
          v-model="paginationPage"
          :length="pageLength"
          :total-visible="7"
          color="primary"
          @input="onPageChange"
        ></v-pagination>
      </div>

    </v-card>
    <create-news-article-dialog
      :visible="isNewsArticleDialogOpen"
      :edit-item="selectedNewsArticleItem"
      @close="closeNewsArticleDialog"
      @saved="onNewsArticleSaved"
    />
    <v-dialog-confirm
      :show="isDeleteDialogOpen"
      :subTitle="deleteConfirmSubTitle()"
      title="Delete Confirmation"
      headlineColor="red"
      confirmBtnColor="red"
      buttonText="Delete"
      :close="closeDeleteDialog"
      :confirm="confirmDeleteArticle"
    />
    <snack-bar v-model="isSuccessSnackbar" :content="snackbarMessage"></snack-bar>
    <snack-bar v-model="isFailSnackbar" :content="snackbarErrorMessage" type="error"></snack-bar>
  
    <v-dialog v-model="isPreviewOpen" max-width="700">
      <v-card class="pa-4" style="border-radius: 14px;">
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="font-weight-bold text-subtitle-1 text-truncate" style="max-width: 85%;">
            {{ previewItem ? (previewItem.title_th || previewItem.title_en) : 'Cover Image Preview' }}
          </div>
          <v-btn icon small @click="isPreviewOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="d-flex align-center justify-center grey lighten-4" style="border-radius: 10px; overflow: hidden; min-height: 280px; max-height: 440px;">
          <v-img
            v-if="previewImageUrl"
            :src="previewImageUrl"
            contain
            max-height="440"
            width="100%"
          ></v-img>
          <div v-else class="text-center py-10 grey--text">
            <v-icon size="48" color="grey lighten-1">mdi-image-off-outline</v-icon>
            <div class="mt-2 text-caption">No Cover Image Uploaded</div>
          </div>
        </div>

        <div class="d-flex align-center justify-space-between mt-3 px-1 text-caption grey--text text--darken-1">
          <span>{{ previewItem ? previewItem.category_name : '' }}</span>
          <span>{{ previewItem ? formatDate(previewItem.publish_date) : '' }}</span>
        </div>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import createNewsArticleDialog from './Edit.vue'
import AlertConfirm from '@/components/AlertConfirm'
import SnackBar from '@/components/SnackBar'
import Vue from 'vue'

export default {
  name: 'NewsList',
  components: {
    createNewsArticleDialog,
    'v-dialog-confirm': AlertConfirm,
    SnackBar
  },
  data() {
    return {    
      breadcrumbs: [
        { text: 'Dashboard', exact: true, href: '/' },
        { text: 'News & Articles', disabled: true, href: '/news-list' }
      ],
      currentRole: 'super_admin',

      searchQuery: '',
      searchDebounceTimer: null,
      selectedCategory: 'all',
      selectedStatus: 'all',
      selectedBranch: '',
      dateMenu: false,
      dateFrom: '',
      dateTo: '',
      pickerDates: [],
      statusOptions: [
        { text: 'All Statuses', value: 'all' },
        { text: 'Published', value: 'published' },
        { text: 'Draft', value: 'draft' },
        { text: 'Scheduled', value: 'scheduled' },
        { text: 'Archived', value: 'archived' }
      ],
      selectedNewsArticleItem: null,
      isNewsArticleDialogOpen: false,
      isDeleteDialogOpen: false,
      deleteTargetItem: null,
      isSuccessSnackbar: false,
      snackbarMessage: '',
      isFailSnackbar: false,
      snackbarErrorMessage: '',
      isPreviewOpen: false,
      previewItem: null,
      loadingEditId: null,
      paginationPage: 1,
      table: {
        pageSize: 25,
        headers: [
          { text: 'NO.', value: 'no', width: '50px', sortable: false, align: 'center' },
          { text: 'ARTICLE', value: 'article', sortable: false, minWidth: '220px' },
          { text: 'BRANCH SCOPE', value: 'org_codename', sortable: false, width: '125px' },
          { text: 'CATEGORY', value: 'category', sortable: false, width: '135px' },
          { text: 'AUTHOR / DOCTOR', value: 'author', sortable: false, width: '130px' },
          { text: 'FEATURED', value: 'is_featured', sortable: false, width: '105px' },
          { text: 'PUBLISH DATE', value: 'publish_date', sortable: false, width: '115px' },
          { text: 'STATUS', value: 'status', sortable: false, width: '120px' },
          { text: 'ACTIONS', value: 'actions', sortable: false, align: 'center', width: '110px' }
        ]
      },
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('news', [
      'newsList',
      'total',
      'isLoading',
      'category',
      'orgList',
      'doctorList'
    ]),

    isSuperAdmin() {
      const u = (this.$store.state.auth && this.$store.state.auth.user) || this.user || {}      
      return Boolean(u.is_superuser)
    },
    isBranchDisabled() {
      return !this.isSuperAdmin
    },
    branchSelectItems() {
      if (this.isBranchDisabled) {
        const userOrg = this.getUserOrgCode() || (this.orgList && this.orgList.all && this.orgList.all[0] && this.orgList.all[0].codename) || ''
        return [{ name: (userOrg || '-').toUpperCase(), codename: userOrg }]
      }
      const allOrgs = (this.orgList && this.orgList.all) || []
      return [
        { name: `All Branches`, codename: '' },
        ...allOrgs.map(org => ({
          name: (org.name || org.codename || '').toUpperCase(),
          codename: org.codename
        }))
      ]
    },

    categoryOptions() {
      const storeCats = this.category || []
      if (storeCats.length > 0) {
        return [
          { text: 'All Categories', value: 'all' },
          ...storeCats
        ]
      }

      const map = new Map();
      (this.newsList || []).forEach(item => {
        if (item.category && item.category.id) {
          const contents = item.category.category_contents || item.category.contents || []
          const th = contents.find(c => c.code === 'th')
          const en = contents.find(c => c.code === 'en')
          const title = (th && th.title) || (en && en.title) || item.category.name || `Category ${item.category.id}`
          const id = String(item.category.id)
          if (!map.has(id)) {
            map.set(id, { text: title, value: id })
          }
        }
      })

      return [
        { text: 'All Categories', value: 'all' },
        ...Array.from(map.values())
      ]
    },


    isArticleListTab() {
      return this.$route.path === '/news-list' || this.$route.name === 'news-list'
    },
    isCategoriesTab() {
      return this.$route.path === '/news-categories' || this.$route.name === 'news-categories'
    },
    formattedNewsList() {
      let list = (this.newsList || []).map(this.mapArticleItem)

       if (this.selectedStatus && this.selectedStatus !== 'all') {
        const s = String(this.selectedStatus).toLowerCase()
        list = list.filter(item => {
          if (s === '0' || s === 'published') return item.status === 'published'
          if (s === '1' || s === 'draft') return item.status === 'draft'
          if (s === '2' || s === 'scheduled') return item.status === 'scheduled'
          if (s === 'archived') return item.status === 'archived'
          return item.status === s
        })
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase()
        list = list.filter(item => {
          const matchTitleTh = (item.title_th || '').toLowerCase().includes(q)
          const matchTitleEn = (item.title_en || '').toLowerCase().includes(q)
          const matchAuthor = (item.author || '').toLowerCase().includes(q)
          const rawTags = item.tags || (item.raw_item && item.raw_item.tags) || []
          const tagsList = Array.isArray(rawTags)
            ? rawTags.map(t => (typeof t === 'object' && t ? (t.name || t.text || t.title || '') : String(t))).join(' ')
            : (typeof rawTags === 'string' ? rawTags : '')
          const matchTags = tagsList.toLowerCase().includes(q)
          return matchTitleTh || matchTitleEn || matchAuthor || matchTags
        })
      }
      if (this.pickerDates && this.pickerDates.length === 2) {
        const sorted = [...this.pickerDates].sort()
        const startMs = new Date(sorted[0] + 'T00:00:00').getTime()
        const endMs = new Date(sorted[1] + 'T23:59:59').getTime()
        list = list.filter(item => {
          const dt = item.publish_date || item.period_start_date || item.update_date
          if (!dt) return false
          const itemMs = new Date(dt).getTime()
          return itemMs >= startMs && itemMs <= endMs
        })
      }
      return list
    },

    totalDisplayCount() {
      const hasDateFilter = this.pickerDates && this.pickerDates.length === 2
      const hasSearch = this.searchQuery && this.searchQuery.trim()
      const hasStatusFilter = this.selectedStatus && this.selectedStatus !== 'all'
      if (hasDateFilter || hasSearch || hasStatusFilter) {
        return this.formattedNewsList.length
      }
      return this.total || 0
    },
    pageLength() {
      const size = (this.table && this.table.pageSize) || 25
      return Math.max(1, Math.ceil(this.totalDisplayCount / size))
    },
    pageStart() {
      if (!this.totalDisplayCount || !this.formattedNewsList.length) return 0
      const size = (this.table && this.table.pageSize) || 25
      return (this.paginationPage - 1) * size + 1
    },
    pageEnd() {
      if (!this.totalDisplayCount || !this.formattedNewsList.length) return 0
      const size = (this.table && this.table.pageSize) || 25
      return Math.min((this.paginationPage - 1) * size + this.formattedNewsList.length, this.totalDisplayCount)
    },
    activeFilterCount() {
      let count = 0
      if (this.searchQuery && this.searchQuery.trim()) count += 1
      if (!this.isBranchDisabled && this.selectedBranch && this.selectedBranch !== 'all' && this.selectedBranch !== '') count += 1
      if (this.selectedCategory && this.selectedCategory !== 'all' && this.selectedCategory !== '') count += 1
      if (this.selectedStatus && this.selectedStatus !== 'all' && this.selectedStatus !== '') count += 1
      if (this.dateFrom || this.dateTo || (this.pickerDates && this.pickerDates.length > 0)) count += 1
      return count
    },

    hasAppliedFilters() {
      return this.activeFilterCount > 0
    },
    previewImageUrl() {
      if (!this.previewItem) return ''
      const item = this.previewItem.raw_item || this.previewItem
      const th = (item.contents || []).find(c => c.code === 'th') || (item.contents && item.contents[0]) || {}      
      const en = (item.contents || []).find(c => c.code === 'en') || {}
      return item.cover_image || th.thumbnail || th.image || en.thumbnail || en.image || ''
    },

  },
  async mounted() {
    if (this.setListOrgInfo) {
      await this.setListOrgInfo()
    }
    if (this.isBranchDisabled) {
      this.currentRole = 'branch_admin'
      this.selectedBranch = this.getUserOrgCode() || (this.orgList && this.orgList.all && this.orgList.all[0] && this.orgList.all[0].codename) || ''
    } else {
      this.currentRole = 'super_admin'
      this.selectedBranch = ''
    }
    this.syncBranchFilterToStore()
    const allBranchCodenames = ((this.orgList && this.orgList.all) || []).map(o => o.codename)
    if (this.setOrgFilterCategory) {
      this.setOrgFilterCategory(this.selectedBranch ? [this.selectedBranch] : allBranchCodenames)
    }
    this.selectedCategory = 'all'
    this.selectedStatus = 'all'
    this.searchQuery = ''
    this.setFilterCategory('')
    this.setFilterStatus('')
    this.setTextSearch('')
    this.setPage(1)
    await Promise.all([
      this.fetchCategory(),
      this.fetch(),
      !(this.doctorList && this.doctorList.length) && this.fetchMasterEntities ? this.fetchMasterEntities() : null    ])
  },
  beforeDestroy() {
    this.setFilterCategory('')
    this.setFilterStatus('')
    this.setTextSearch('')
  },
  methods: {
    ...mapActions('news', [
      'fetch',
      'fetchCategory',
      'setListOrgInfo',
      'setTextSearch',
      'setFilterStatus',
      'setFilterCategory',
      'setOrgFilterCategory',
      'setPage',
      'setDeleteId',
      'deletePkg',
      'setNewsId',
      'fetchMasterEntities'
      
    ]),
    getUserOrgCode() {
      const u = this.user || {}
      const code = u.org_code || u.org_codename || u.org || u.assigned_branch || (u.organization && u.organization.codename)
      return (code || '').toLowerCase()
    },
    syncBranchFilterToStore() {
      if (this.$store.state.news && this.$store.state.news.orgList) {
        this.$store.state.news.orgList.selected = this.selectedBranch
          ? [{ codename: this.selectedBranch, selected: true }]
          : []
      }
    },

    mapArticleItem(item) {
      const thContent = (item.contents || []).find(c => c.code === 'th') || (item.contents && item.contents[0]) || {}     
      const enContent = (item.contents || []).find(c => c.code === 'en') || {}
      let categoryName = '-'
      if (item.category) {
        const catContents = item.category.category_contents || item.category.contents || []
        if (catContents.length) {
          const catTh = catContents.find(c => c.code === 'th')
          const catEn = catContents.find(c => c.code === 'en')
          categoryName = (catTh && catTh.title) || (catEn && catEn.title) || catContents[0].title || '-'
        } else if (item.category.name || item.category.title) {
          categoryName = item.category.name || item.category.title
        }
      }

      let orgCodename = 'central'
      const allOrgsCount = (this.orgList && this.orgList.all && this.orgList.all.length) || 0
      const itemOrg = (item.org_codename || '').toLowerCase()
      if (itemOrg === 'central' || itemOrg === 'princgroup') {
        orgCodename = 'central'
      } else if (item.orgs && item.orgs.length) {
        const isCentral = item.orgs.some(o => {
          const c = (o.org_codename || o.codename || '').toLowerCase()
          return c === 'central' || c === 'princgroup'
        })
        if (isCentral || (allOrgsCount > 0 && item.orgs.length >= allOrgsCount)) {
          orgCodename = 'central'
        } else {
          orgCodename = item.orgs.map(o => (o.org_codename || o.codename || '').toUpperCase()).join(', ')
        }
      } else {
        orgCodename = itemOrg ? itemOrg.toUpperCase() : 'central'
      }

      let authorName = '-'
      const primaryDocId = item.doctor_id || (item.doctor && (item.doctor.id || item.doctor.doctor_id))
      
      if (primaryDocId && this.doctorList && this.doctorList.length) {
        const doc = this.doctorList.find(d => String(d.id) === String(primaryDocId))
        if (doc && doc.name) {
          authorName = doc.name
        }
      } else if (item.doctor && item.doctor.name) {
        authorName = item.doctor.name
      } else if ((item.create_by_user && item.create_by_user.name) || (item.update_by_user && item.update_by_user.name)) {
        authorName = (item.create_by_user && item.create_by_user.name) || (item.update_by_user && item.update_by_user.name)
      }

      let statusKey = 'draft'
      const now = new Date()
      const rawPubDate = item.public_date || item.publish_date || item.period_start_date
      let pubDate = null
      if (rawPubDate) {
        if (item.period_start_time && typeof rawPubDate === 'string' && rawPubDate.length === 10) {
          pubDate = new Date(`${rawPubDate}T${item.period_start_time}`)
        } else {
          pubDate = new Date(rawPubDate)
        }
      }

      const endDate = item.period_end_date ? new Date(item.period_end_date) : null
      const isExpired = endDate && !isNaN(endDate.getTime()) && endDate.getFullYear() < 2090 && endDate.getTime() < now.getTime()

      if (item.status === 1) {
        if (isExpired) {
          statusKey = 'archived'
        } else {
          statusKey = 'draft'
        }
      } else if (item.status === 0 || item.status === 2) {
        if (pubDate && !isNaN(pubDate.getTime()) && pubDate.getTime() > now.getTime()) {
          statusKey = 'scheduled'
        } else if (isExpired) {
          statusKey = 'archived'
        } else {
          statusKey = 'published'
        }
      }

      return {
        ...item,
        raw_item: item,
        id: item.id,
        title_th: thContent.title || 'Untitled',
        title_en: enContent.title || '',
        org_codename: orgCodename,
        category_name: categoryName,
        author: authorName,
        is_featured: !!item.is_featured || (item.related_campaigns && item.related_campaigns.length > 0) || false,
        publish_date: item.public_date || item.period_start_date || item.update_date,
        tags: item.tags || [],
        status: statusKey
      }
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.handleSearch()
      }, 400)
    },
    async handleSearch() {
      this.paginationPage = 1
      this.setPage(1)
      this.setTextSearch(this.searchQuery)
      await this.fetch()
      if (this.total === 0 && this.searchQuery && this.searchQuery.trim()) {
        this.setTextSearch('')
        await this.fetch()
      }
    },
    async clearSearch() {
      clearTimeout(this.searchDebounceTimer)
      this.searchQuery = ''
      this.paginationPage = 1
      this.setTextSearch('')
      this.setPage(1)
      await this.fetch()
    },


    async handleBranchChange() {
      this.paginationPage = 1
      this.selectedCategory = 'all'
      this.syncBranchFilterToStore()

      const filterBranches = this.selectedBranch
        ? [this.selectedBranch]
        : ((this.orgList && this.orgList.all) || []).map(o => o.codename)

      if (this.setOrgFilterCategory) {
        this.setOrgFilterCategory(filterBranches)
      }
      this.setFilterCategory('')
      await this.fetchCategory()

      this.setPage(1)
      await this.fetch()
    },

    
    handleFilterChange() {
      this.paginationPage = 1
      let Status = ''
      if (this.selectedStatus === '1' || this.selectedStatus === 'draft') {
        Status = 1
      } else if (this.selectedStatus === '0' || this.selectedStatus === 'published') {
        Status = 0
      }
      this.setFilterStatus(Status)
      this.setFilterCategory(this.selectedCategory)
      this.setPage(1)
      this.fetch()
    },

    onDateRangeChange() {
      if (this.pickerDates && this.pickerDates.length === 2) {
        const sorted = [...this.pickerDates].sort()
        this.dateFrom = this.formatDate(sorted[0])
        this.dateTo = this.formatDate(sorted[1])
        this.dateMenu = false
      } else if (this.pickerDates && this.pickerDates.length === 1) {
        this.dateFrom = this.formatDate(this.pickerDates[0])
        this.dateTo = ''
      }
    },
    onPageChange(newPage) {
      this.paginationPage = newPage
      this.setPage(newPage)
      this.fetch()
    },

    getRowIndex(item) {
      const idx = (this.formattedNewsList || []).indexOf(item)
      const size = (this.table && this.table.pageSize) || 25
      return idx >= 0 ? (this.paginationPage - 1) * size + idx + 1 : '-'
    },
    goToArticlesTab() {
      if (this.$route.path !== '/news-list') {
        this.$router.push('/news-list').catch(() => {})
      }
    },
    goToCategoriesTab() {
      if (this.$route.path !== '/news-categories') {
        this.$router.push('/news-categories').catch(() => {})
      }
    },

    goToCreateArticle() {
      this.selectedNewsArticleItem = null
      this.isNewsArticleDialogOpen = true
    },
    async handleEditNewsArticle(item) {
      this.loadingEditId = item.id
      try {
        const detail = await this.setNewsId(item.id)
        this.selectedNewsArticleItem = detail || item.raw_item || item
        this.isNewsArticleDialogOpen = true
      } catch (err) {
        Vue.$log.error('Failed to load article details:', err)
        this.selectedNewsArticleItem = item.raw_item || item
        this.isNewsArticleDialogOpen = true
      } finally {
        this.loadingEditId = null
      }
    },
    closeNewsArticleDialog() {
      this.isNewsArticleDialogOpen = false
      this.selectedNewsArticleItem = null
    },
    async onNewsArticleSaved() {
      this.closeNewsArticleDialog()
      this.showSuccessSnackbar('Article saved successfully')
      await this.fetch()
      this.$nextTick(() => {
        const tableWrapper = document.querySelector('.news-table .v-data-table__wrapper')
        if (tableWrapper) tableWrapper.scrollTop = 0
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    },
    handlePreview(item) {
      this.previewItem = item
      this.isPreviewOpen = true
    },
    handleDelete(item) {
      this.deleteTargetItem = item
      this.isDeleteDialogOpen = true
    },
     closeDeleteDialog() {
      this.isDeleteDialogOpen = false
      this.deleteTargetItem = null
    },
    async confirmDeleteArticle() {
      if (!this.deleteTargetItem) return
      try {
        this.setDeleteId(this.deleteTargetItem.id)
        const success = await this.deletePkg()
        if (success) {
          this.showSuccessSnackbar('Article removed successfully')
          await this.fetch()
        } else {
          this.showFailSnackbar('Failed to delete article')
        }
      } catch (err) {
        this.showFailSnackbar('Failed to delete article')
      } finally {
        this.closeDeleteDialog()
      }
    },
    deleteConfirmSubTitle() {
      const name = this.deleteTargetItem
        ? (this.deleteTargetItem.title_en || this.deleteTargetItem.title_th || 'this article')
        : 'this article'
      return `Are you sure you want to delete <b>"${name}"</b>?`
    },
    showSuccessSnackbar(msg) {
      this.snackbarMessage = msg
      this.isSuccessSnackbar = true
    },
    showFailSnackbar(msg) {
      this.snackbarErrorMessage = msg
      this.isFailSnackbar = true
    },
    isCentralOrg(code) {
      if (!code) return true
      const c = String(code).toLowerCase()
      return c === 'central' || c === 'princgroup' || c === 'all'
    },
    getStatusColor(status) {
      const map = { published: 'green', scheduled: 'blue', draft: 'orange', archived: 'gray' }
      return map[status] || 'gray'
    },
    formatStatusLabel(status) {
      if (!status) return '-'
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    getArticleColor(item) {
      const colors = ['#1d4ed8', '#7c3aed', '#0f172a', '#d97706', '#059669']
      const hash = (item.title_en || item.title_th || '').length % colors.length
      return colors[hash]
    },
    formatDate(dt) {
      if (!dt) return '—'
      const d = new Date(dt)
      if (isNaN(d.getTime())) return dt
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
    },
    formatTime(dt) {
      if (!dt) return ''
      const d = new Date(dt)
      if (isNaN(d.getTime())) return ''
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },

    async clearAllFilters() {
      clearTimeout(this.searchDebounceTimer)
      this.searchQuery = ''
      this.selectedCategory = 'all'
      this.selectedStatus = 'all'
      this.dateFrom = ''
      this.dateTo = ''
      this.pickerDates = []

      if (!this.isBranchDisabled) {
        this.selectedBranch = ''
        this.syncBranchFilterToStore()
      }

      this.setTextSearch('')
      this.setFilterStatus('')
      this.setFilterCategory('')
      this.paginationPage = 1
      this.setPage(1)

      const allBranchCodenames = ((this.orgList && this.orgList.all) || []).map(o => o.codename)
      if (this.setOrgFilterCategory) {
        this.setOrgFilterCategory(this.selectedBranch ? [this.selectedBranch] : allBranchCodenames)
      }

      await Promise.all([
        this.fetchCategory(),
        this.fetch()
      ])
    },

  }
}
</script>


<style scoped>
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.pageHeadline { font-size: 22px; font-weight: 700; color: #0f172a; }
.thai-subtitle { font-weight: 400; font-size: 15px; color: #64748b; margin-left: 8px; }
.page-description { font-size: 13px; color: #64748b; }


.add-btn { text-transform: none; font-weight: 600; border-radius: 8px; }

.pill-tabs-bar { display: flex; align-items: center; gap: 10px; }
.tab-pill {
  padding: 8px 20px;
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  color: #475569;
  outline: none;
  transition: all 0.2s ease;
}
.tab-pill:hover { background-color: #f8fafc; color: #0f172a; }
.tab-pill.active {
  background-color: #eef4ff;
  border-color: #c7d9fc;
  color: #1e40af;
  font-weight: 600;
}

.table-card {
  border: 1px solid #e6e9f0 !important;
  border-radius: 10px !important;
  overflow: hidden;
  background-color: #ffffff !important;
}
.filter-bar { border-bottom: 1px solid #eef2f7; }
.filter-row { display: flex; align-items: center; flex-wrap: nowrap; width: 100%; }
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }

.filter-search-field { min-width: 250px; }
.filter-select-field { flex: 0 0 auto; }
.filter-select-field.branch-select { width: 190px; }
.filter-select-field.category-select { width: 160px; }
.filter-select-field.status-select { width: 140px; }

.date-range-container {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  padding: 0 12px;
  height: 40px !important;
  min-height: 40px !important;
  width: 250px;
  box-sizing: border-box;
}
.date-icon { color: #64748b !important; }
.date-input { border: none; background: transparent; outline: none; font-size: 13px !important; width: 90px; color: #0f172a; text-align: center; }
.date-separator { color: #94a3b8; font-size: 13px; }

.filter-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-color: #e2e8f0 !important;
  height: 40px !important;
  border-radius: 8px !important;
  white-space: nowrap !important;
}
.filter-btn.has-filtered {
  color: #ef4444 !important;
  border-color: #fca5a5 !important;
  background-color: #fef2f2 !important;
}
.filter-btn.has-filtered:hover {
  background-color: #fee2e2 !important;
  border-color: #f87171 !important;
}

.news-table {
  width: 100%;
}

.news-table ::v-deep thead tr:not(.v-data-table__progress) th {
  font-size: 12px !important;
  color: #64748b !important;
  font-weight: 700 !important;
  background-color: #ffffff !important;
  border-bottom: 1px solid #eef2f7 !important;
  height: 46px !important;
}

.news-table ::v-deep .v-data-table__progress th {
  height: 0 !important;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}

.news-table ::v-deep .v-data-table__progress {
  height: 3px !important;
}

.news-table ::v-deep td {
  font-size: 13.5px !important;
  color: #0f172a !important;
  height: 56px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.news-table ::v-deep tbody tr {
  transition: background-color 0.15s ease;
}

.news-table ::v-deep tbody tr:hover {
  background-color: #f8fafc !important;
}

.news-table ::v-deep .v-data-table__wrapper {
  max-height: calc(100vh - 330px);
  overflow-y: auto;
  overflow-x: auto;
}

.news-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.news-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #f8fafc;
}

.news-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.news-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  font-size: 12px;
  color: #64748b;
  border-top: 1px solid #eef2f7;
}

.footer-entries {
  font-size: 12.5px;
  color: #64748b;
}

.no-text { font-size: 13px; color: #64748b; font-weight: 500; }
.article-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.article-title-th { font-size: 13.5px; font-weight: 700; color: #0f172a; line-height: 1.3; }
.article-title-en { font-size: 12px; color: #64748b; line-height: 1.2; }

.branch-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: 0.2px;
  min-height: 26px;
  box-sizing: border-box;
}
.branch-tag.central {
  background: #f3e8ff;
  color: #7c3aed;
  text-align: center;
  line-height: 1.2;
}
.branch-tag.branch {
  background: #eef1f6;
  color: #5b6472;
  white-space: nowrap;
}
.category-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 6px;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
  min-height: 26px;
}
.featured-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  min-height: 24px;
}
.featured-pill.pinned {
  background-color: #fef3c7;
  color: #b45309;
  font-weight: 700;
}
.featured-pill.standard {
  background-color: #f1f5f9;
  color: #64748b;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 11.5px;
  white-space: nowrap;
  min-height: 24px;
}
.status-pill .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.status-pill.status-green { background: #f0fdf4; color: #15803d; }
.status-pill.status-green .dot { background: #1aa964; }
.status-pill.status-blue { background: #eff6ff; color: #2563eb; }
.status-pill.status-blue .dot { background: #3b82f6; }
.status-pill.status-orange { background: #fffbeb; color: #b45309; }
.status-pill.status-orange .dot { background: #f59e0b; }
.status-pill.status-gray { background: #f8fafc; color: #64748b; }
.status-pill.status-gray .dot { background: #94a3b8; }
.action-icon-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-icon-btn:hover { background: #f8fafc; border-color: #cbd5e1; color: #0f172a; }

</style>
